import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { projects as projectsApi } from '@/utils/api';
import { CircleDot, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const COLUMNS = [
  'New', 'Scoping', 'Estimate Sent', 'Approved', 'In Progress', 'UAT', 'Delivered', 'Archived',
];

const COL_COLORS = {
  'New': '#5C6B72',
  'Scoping': '#006080',
  'Estimate Sent': '#D9A441',
  'Approved': '#3C8A5A',
  'In Progress': '#006080',
  'UAT': '#0066CC',
  'Delivered': '#3C8A5A',
  'Archived': '#8A99A0',
};

function SortableProjectCard({ project, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: project.id,
    data: { project },
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="card-interactive p-3 cursor-grab active:cursor-grabbing"
      onClick={onClick}
      data-testid={`pipeline-card-${project.id}`}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-semibold leading-tight" style={{ color: 'var(--ink)' }}>{project.name}</p>
        <GripVertical size={14} style={{ color: 'var(--border)', flexShrink: 0 }} />
      </div>
      <p className="text-xs mb-2" style={{ color: 'var(--muted)' }}>
        {project.client_name || 'Unknown client'}
      </p>
      <div className="flex items-center justify-between">
        <span
          className="text-xs font-medium px-2 py-0.5 rounded-full"
          style={{ background: `${COL_COLORS[project.status] || '#006080'}15`, color: COL_COLORS[project.status] || '#006080' }}
        >
          {project.project_type}
        </span>
        {project.brand_name && (
          <span className="text-xs" style={{ color: 'var(--muted)' }}>{project.brand_name}</span>
        )}
      </div>
    </div>
  );
}

function Column({ col, projects, onCardClick }) {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${col}`, data: { col } });
  return (
    <div key={col} className="w-64 flex-shrink-0" data-testid={`pipeline-column-${col}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CircleDot size={12} style={{ color: COL_COLORS[col] }} />
          <span className="text-xs font-bold uppercase tracking-wide" style={{ color: COL_COLORS[col] }}>{col}</span>
        </div>
        <span
          className="text-xs font-bold px-1.5 py-0.5 rounded-full"
          style={{ background: `${COL_COLORS[col]}15`, color: COL_COLORS[col] }}
        >
          {projects.length}
        </span>
      </div>
      <SortableContext items={projects.map(p => p.id)} strategy={verticalListSortingStrategy}>
        <div
          ref={setNodeRef}
          className="space-y-2 min-h-16 rounded-card transition-colors"
          style={{ background: isOver ? `${COL_COLORS[col]}0A` : 'transparent', padding: isOver ? 4 : 0 }}
        >
          {projects.map(p => (
            <SortableProjectCard
              key={p.id}
              project={p}
              onClick={() => p.client_id && onCardClick(p)}
            />
          ))}
          {projects.length === 0 && (
            <div
              className="h-16 rounded-card border-2 border-dashed flex items-center justify-center"
              style={{ borderColor: 'var(--border)' }}
            >
              <span className="text-xs" style={{ color: 'var(--muted)' }}>Empty</span>
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
}

export default function Pipeline() {
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const navigate = useNavigate();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    projectsApi.list()
      .then(r => setAllProjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => COLUMNS.reduce((acc, col) => {
    acc[col] = allProjects.filter(p => p.status === col);
    return acc;
  }, {}), [allProjects]);

  const activeProject = activeId ? allProjects.find(p => p.id === activeId) : null;

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const project = allProjects.find(p => p.id === active.id);
    if (!project) return;

    // Target column is either a droppable col id (col:New) or another card whose status we read
    let toStatus = null;
    if (String(over.id).startsWith('col:')) {
      toStatus = String(over.id).slice(4);
    } else {
      const overProj = allProjects.find(p => p.id === over.id);
      if (overProj) toStatus = overProj.status;
    }
    if (!toStatus || toStatus === project.status) return;

    // Optimistic update
    const prev = allProjects;
    setAllProjects(ps => ps.map(p => p.id === project.id ? { ...p, status: toStatus } : p));
    try {
      await projectsApi.updateStatus(project.id, toStatus);
      toast.success(`Moved to ${toStatus}`);
    } catch (e) {
      setAllProjects(prev);
      toast.error(e.response?.data?.detail || 'Failed to move card');
    }
  };

  return (
    <div className="p-6 animate-fade-in">
      <div className="mb-4">
        <h1 className="text-xl font-black" style={{ color: 'var(--ink)' }}>Pipeline</h1>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>
          {allProjects.length} project{allProjects.length !== 1 ? 's' : ''} — drag cards between columns to change status
        </p>
      </div>

      {loading ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.slice(0, 5).map(col => (
            <div key={col} className="w-64 flex-shrink-0">
              <div className="h-8 rounded-btn mb-3 animate-pulse" style={{ background: 'var(--border)' }} />
              {[1, 2].map(i => (
                <div key={i} className="h-24 rounded-card mb-2 animate-pulse" style={{ background: 'var(--border)' }} />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={(e) => setActiveId(e.active.id)}
          onDragCancel={() => setActiveId(null)}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
            {COLUMNS.map(col => (
              <Column
                key={col}
                col={col}
                projects={grouped[col] || []}
                onCardClick={(p) => navigate(`/admin/clients/${p.client_id}/projects/${p.id}`)}
              />
            ))}
          </div>
          <DragOverlay>
            {activeProject && (
              <div className="card-interactive p-3 opacity-90 shadow-2xl w-64">
                <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{activeProject.name}</p>
                <p className="text-xs" style={{ color: 'var(--muted)' }}>{activeProject.client_name}</p>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}
